import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FaYoutube } from 'react-icons/fa';
import { FiSearch, FiEye, FiThumbsUp, FiMessageCircle, FiArrowUp, FiArrowDown, FiMinus } from 'react-icons/fi';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import axios from '../../../../utilities/axios';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const formatNumber = (n) => {
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
};

const formatSnapshotLabel = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    + ' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const Page = styled.div`
  padding: 28px 24px;
  max-width: 860px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  .icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(255,0,0,0.12); border: 1px solid rgba(255,0,0,0.25);
    display: flex; align-items: center; justify-content: center;
    svg { color: #ff4d4d; font-size: 20px; }
  }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;

const SearchForm = styled.form`
  display: flex;
  gap: 10px;
  margin-bottom: 22px;

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 13px 16px;
  border-radius: 10px;
  border: 1.5px solid rgba(255,255,255,0.08);
  background: #1A1A1A;
  color: #e2e8f0;
  font-size: 13.5px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
  &::placeholder { color: #6b7280; }
  &:focus { border-color: rgba(255,77,77,0.5); }
`;

const TrackBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 13px 24px; border-radius: 10px; border: none;
  font-family: inherit; font-size: 13.5px; font-weight: 700;
  cursor: pointer; transition: opacity 0.15s;
  background: linear-gradient(135deg, #ff4d4d, #e60000);
  color: #fff;
  opacity: ${p => p.disabled ? 0.55 : 1};
  pointer-events: ${p => p.disabled ? 'none' : 'auto'};
  &:hover { opacity: 0.88; }
`;

const ErrorBox = styled.div`
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(239,68,68,0.1);
  border: 1px solid rgba(239,68,68,0.3);
  color: #f87171;
  font-size: 12.5px;
`;

const VideoInfoCard = styled.div`
  display: flex;
  gap: 14px;
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;

  .thumb {
    width: 140px;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
    background: #0B090B;
  }

  .meta {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
  }

  .meta-title {
    font-size: 14.5px;
    font-weight: 700;
    color: #f1f5f9;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .channel {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;

    img {
      width: 22px; height: 22px; border-radius: 50%; object-fit: cover;
      background: #0B090B;
    }
    span { font-size: 12px; color: #9ca3af; font-weight: 600; }
  }

  @media (max-width: 560px) {
    .thumb { width: 100px; }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 16px;

  .top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .icon {
    width: 32px; height: 32px; border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
    background: ${p => p.accent}22;
    svg { color: ${p => p.accent}; font-size: 15px; }
  }

  .delta {
    display: flex; align-items: center; gap: 3px;
    font-size: 11.5px; font-weight: 800;
    padding: 3px 7px; border-radius: 999px;
    color: ${p => p.deltaColor};
    background: ${p => p.deltaColor}1a;
    svg { font-size: 11px; }
  }

  .value {
    margin-top: 14px;
    font-size: 24px;
    font-weight: 800;
    color: #f1f5f9;
  }

  .label {
    margin-top: 2px;
    font-size: 11.5px;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const ChartCard = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 20px 18px;

  .chart-title {
    font-size: 13px;
    font-weight: 700;
    color: #9ca3af;
    margin-bottom: 14px;
  }

  .chart-wrap {
    height: 280px;
  }

  .hint {
    margin-top: 12px;
    font-size: 11.5px;
    color: #6b7280;
    text-align: center;
  }
`;

const Delta = ({ current, previous, deltaColorMap }) => {
  const { t } = useTranslation();
  if (previous == null) {
    return (
      <span className="delta" style={{ color: '#6b7280', background: 'rgba(107,114,128,0.15)' }}>
        <FiMinus /> {t('website.youtube_tool.no_change')}
      </span>
    );
  }
  const diff = current - previous;
  if (diff === 0) {
    return <span className="delta" style={{ color: '#6b7280', background: 'rgba(107,114,128,0.15)' }}><FiMinus /> 0</span>;
  }
  const isUp = diff > 0;
  const pct = previous > 0 ? Math.abs((diff / previous) * 100) : 0;
  const color = isUp ? '#22c55e' : '#ef4444';
  return (
    <span className="delta" style={{ color, background: `${color}1a` }}>
      {isUp ? <FiArrowUp /> : <FiArrowDown />}
      {formatNumber(Math.abs(diff))} ({pct.toFixed(1)}%)
    </span>
  );
};

const YoutubeTool = () => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post('/api/track_youtube_video', { url: url.trim() });
      setResult(data.data);
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || t('website.youtube_tool.generic_error'));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const snapshots = result?.snapshots || [];
  const latest = snapshots[snapshots.length - 1];
  const previous = snapshots.length > 1 ? snapshots[snapshots.length - 2] : null;

  const chartData = useMemo(() => {
    if (snapshots.length === 0) return null;
    return {
      labels: snapshots.map(s => formatSnapshotLabel(s.captured_at)),
      datasets: [
        {
          label: t('website.youtube_tool.views_label'),
          data: snapshots.map(s => s.views),
          borderColor: '#a78bfa',
          backgroundColor: 'rgba(167,139,250,0.15)',
          yAxisID: 'y',
          tension: 0.3,
          fill: true,
          pointRadius: 3,
        },
        {
          label: t('website.youtube_tool.likes_label'),
          data: snapshots.map(s => s.likes),
          borderColor: '#22c55e',
          backgroundColor: 'rgba(34,197,94,0.15)',
          yAxisID: 'y1',
          tension: 0.3,
          fill: true,
          pointRadius: 3,
        },
      ],
    };
  }, [snapshots, t]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { labels: { color: '#9ca3af', boxWidth: 10, font: { size: 11.5 } } },
      tooltip: { backgroundColor: '#0B090B', borderColor: 'rgba(255,255,255,0.1)', borderWidth: 1 },
    },
    scales: {
      x: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#6b7280', font: { size: 10.5 } } },
      y: {
        position: 'left',
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: '#a78bfa', font: { size: 10.5 }, callback: (v) => formatNumber(v) },
      },
      y1: {
        position: 'right',
        grid: { drawOnChartArea: false },
        ticks: { color: '#22c55e', font: { size: 10.5 }, callback: (v) => formatNumber(v) },
      },
    },
  };

  return (
    <WebsiteLayout>
      <Page>
        <PageHeader>
          <div className="icon"><FaYoutube /></div>
          <div>
            <div className="title">{t('website.youtube_tool.title')}</div>
            <div className="sub">{t('website.youtube_tool.sub')}</div>
          </div>
        </PageHeader>

        <SearchForm onSubmit={handleSubmit}>
          <SearchInput
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('website.youtube_tool.input_placeholder')}
          />
          <TrackBtn type="submit" disabled={loading}>
            <FiSearch /> {loading ? t('website.youtube_tool.loading') : t('website.youtube_tool.track_btn')}
          </TrackBtn>
        </SearchForm>

        {error && <ErrorBox>{error}</ErrorBox>}

        {result && (
          <>
            <VideoInfoCard>
              {result.video.thumbnail && <img className="thumb" src={result.video.thumbnail} alt={result.video.title} />}
              <div className="meta">
                <div className="meta-title">{result.video.title}</div>
                <div className="channel">
                  {result.video.channel_avatar && <img src={result.video.channel_avatar} alt={result.video.channel_title} />}
                  <span>{result.video.channel_title}</span>
                </div>
              </div>
            </VideoInfoCard>

            <StatsGrid>
              <StatCard accent="#a78bfa" deltaColor="#22c55e">
                <div className="top-row">
                  <div className="icon"><FiEye /></div>
                  <Delta current={latest?.views ?? 0} previous={previous?.views ?? null} />
                </div>
                <div className="value">{formatNumber(latest?.views ?? 0)}</div>
                <div className="label">{t('website.youtube_tool.views_label')}</div>
              </StatCard>

              <StatCard accent="#22c55e" deltaColor="#22c55e">
                <div className="top-row">
                  <div className="icon"><FiThumbsUp /></div>
                  <Delta current={latest?.likes ?? 0} previous={previous?.likes ?? null} />
                </div>
                <div className="value">{formatNumber(latest?.likes ?? 0)}</div>
                <div className="label">{t('website.youtube_tool.likes_label')}</div>
              </StatCard>

              <StatCard accent="#facc15" deltaColor="#22c55e">
                <div className="top-row">
                  <div className="icon"><FiMessageCircle /></div>
                  <Delta current={latest?.comments ?? 0} previous={previous?.comments ?? null} />
                </div>
                <div className="value">{formatNumber(latest?.comments ?? 0)}</div>
                <div className="label">{t('website.youtube_tool.comments_label')}</div>
              </StatCard>
            </StatsGrid>

            <ChartCard>
              <div className="chart-title">{t('website.youtube_tool.chart_title')}</div>
              <div className="chart-wrap">
                {chartData && <Line data={chartData} options={chartOptions} />}
              </div>
              {snapshots.length < 2 && <div className="hint">{t('website.youtube_tool.single_snapshot_hint')}</div>}
            </ChartCard>
          </>
        )}
      </Page>
    </WebsiteLayout>
  );
};

export default YoutubeTool;
